/* ==========================================================================
   Reset
   ========================================================================== */

.media-minimal-skin *,
.media-minimal-skin *::before,
.media-minimal-skin *::after {
  box-sizing: border-box;
}
.media-minimal-skin img,
.media-minimal-skin video,
.media-minimal-skin svg {
  display: block;
  max-width: 100%;
}
.media-minimal-skin button {
  font: inherit;
}
.media-minimal-skin [hidden][hidden] {
  /* Keep authored templates hidden even when component classes set display. */
  display: none;
}
@media (prefers-reduced-motion: no-preference) {
  .media-minimal-skin {
    interpolate-size: allow-keywords;
  }
}

/* ==========================================================================
   Root Container
   ========================================================================== */

.media-minimal-skin {
  --media-current-shadow-color: oklch(from currentColor 0 0 0 / clamp(0, calc((l - 0.5) * 0.5), 0.15));
  --media-current-shadow-color-subtle: oklch(from var(--media-current-shadow-color) l c h / calc(alpha * 0.4));
  --media-icon-size: 18px;
  --media-color-scrollbar-thumb: oklch(from currentColor l c h / 0.3);
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  container: media-root / inline-size;
  font-family:
    Inter Variable,
    Inter,
    ui-sans-serif,
    system-ui,
    sans-serif;
  font-size: 0.8125rem; /* 13px at 100% font size */
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  line-height: 1.5;
  letter-spacing: normal;
  outline: 2px solid transparent;
  outline-offset: -4px;
  scrollbar-color: var(--media-color-scrollbar-thumb) transparent;
  scrollbar-width: thin;
  border-radius: var(--media-border-radius, 0);
  isolation: isolate;
  transition-timing-function: ease-out;
  transition-duration: 100ms;
  transition-property: outline-offset, outline-color;

  &:focus-visible {
    outline-color: currentColor;
    outline-offset: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--media-color-scrollbar-thumb);
    border-radius: 9999px;
  }

  @media (prefers-reduced-transparency: reduce) or (prefers-contrast: more) {
    --media-color-scrollbar-thumb: oklch(from currentColor l c h / 0.8);
    scrollbar-width: auto;
  }
}

/*
 * A coarse-pointer gesture that begins inside the web video belongs to the
 * player, not the surrounding React Native Web ScrollView. Wheel/trackpad
 * scrolling on desktop is unaffected by touch-action.
 */
@media (pointer: coarse) {
  .media-minimal-skin--video {
    touch-action: none;
  }
}

/* ==========================================================================
   Media Element
   ========================================================================== */

.media-minimal-skin ::slotted(video),
.media-minimal-skin video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: var(--media-object-fit, contain);
  object-position: var(--media-object-position, center);
}
.media-minimal-skin ::slotted(video) {
  border-radius: var(--media-video-border-radius);
}
.media-minimal-skin video {
  border-radius: inherit;
}

.media-minimal-skin:fullscreen ::slotted(video),
.media-minimal-skin:fullscreen video {
  object-fit: contain;
}

/* ==========================================================================
   Overlay / Scrim
   ========================================================================== */

.media-minimal-skin .media-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(to top, oklch(0 0 0 / 0.7), oklch(0 0 0 / 0.5) 7.5rem, oklch(0 0 0 / 0));
  border-radius: inherit;
  opacity: 0;
  backdrop-filter: blur(0) saturate(1);
  transition-timing-function: ease-out;
  transition-duration: var(--media-controls-transition-duration);
  transition-property: opacity, backdrop-filter;
}

.media-minimal-skin .media-error ~ .media-overlay {
  transition-delay: var(--media-error-dialog-transition-delay);
  transition-duration: var(--media-error-dialog-transition-duration);
}

.media-minimal-skin .media-controls[data-visible] ~ .media-overlay,
.media-minimal-skin .media-error[data-open] ~ .media-overlay {
  opacity: 1;
}

.media-minimal-skin .media-buffering-indicator[data-visible] ~ .media-overlay {
  background: oklch(0 0 0 / 0.35);
  opacity: 1;
  backdrop-filter: blur(8px);
}

.media-minimal-skin .media-error[data-open] ~ .media-overlay {
  backdrop-filter: blur(16px) saturate(1.2);
}

/* ==========================================================================
   Buffering Indicator
   ========================================================================== */

.media-minimal-skin .media-buffering-indicator {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: none;
  place-content: center;
  color: oklch(1 0 0);
  pointer-events: none;

  &:not([data-visible]) {
    --media-spinner-animation: none;
  }

  &[data-visible] {
    display: grid;
  }
}

/* ==========================================================================
   Error Dialog
   ========================================================================== */

.media-minimal-skin .media-error:not([data-open]) {
  display: none;
}

.media-minimal-skin .media-error__title {
  font-weight: 600;
  line-height: 1.25;
}

.media-minimal-skin .media-error__description {
  overflow-wrap: anywhere;
  opacity: 0.7;
}

.media-minimal-skin .media-error__actions {
  display: flex;
  gap: 0.5rem;

  & > * {
    flex: 1;
  }
}

.media-minimal-skin .media-error[data-open] ~ .media-controls * {
  visibility: hidden;
}

/* ==========================================================================
   Controls
   ========================================================================== */

.media-minimal-skin .media-controls {
  display: flex;
  align-items: center;
  container: media-controls / inline-size;
  text-shadow: 0 1px 0 var(--media-current-shadow-color);
  background-color: var(--media-controls-background-color);
  backdrop-filter: var(--media-controls-backdrop-filter);
}

/* ==========================================================================
   Time Controls & Display
   ========================================================================== */


.media-minimal-skin .media-time-group {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.media-minimal-skin .media-time {
  font-variant-numeric: tabular-nums;
}

.media-minimal-skin .media-time[role="button"] {
  cursor: pointer;
  outline: 2px solid transparent;
  outline-offset: -2px;
  border-radius: 0.25rem;
  transition-timing-function: ease-out;
  transition-duration: 100ms;
  transition-property: outline-color, outline-offset;

  @supports (corner-shape: squircle) {
    border-radius: 1rem;
    corner-shape: squircle;
  }

  &:focus-visible {
    outline-color: currentColor;
    outline-offset: 2px;
  }
}



/* ==========================================================================
   Buttons
   ========================================================================== */

/* Base button */
.media-minimal-skin .media-button {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 0.5rem 1rem;
  text-align: center;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  outline: 2px solid transparent;
  outline-offset: -2px;
  border: none;
  border-radius: 0.5rem;
  transition-timing-function: ease-out;
  transition-duration: 150ms;
  transition-property: background-color, outline-offset, scale;
  /* Fix weird jumping when clicking on the buttons in Safari. */
  will-change: scale;

  &:focus-visible {
    outline-color: currentColor;
    outline-offset: 2px;
  }

  &:active {
    scale: 0.98;
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
    filter: grayscale(1);
  }

  &[data-availability="unavailable"],
  &[data-availability="unsupported"] {
    display: none;
  }
}

@supports (corner-shape: squircle) {
  .media-minimal-skin .media-button {
    border-radius: 1rem;
    corner-shape: squircle;
  }
}

/* Primary button variant */
.media-minimal-skin .media-button--primary {
  font-weight: 500;
  color: oklch(0 0 0);
  text-shadow: none;
  background: oklch(1 0 0);
}

/* Subtle button variant */
.media-minimal-skin .media-button--subtle {
  color: inherit;
  text-shadow: inherit;
  background: transparent;

  &:hover,
  &:focus-visible,
  &[aria-expanded="true"] {
    background: oklch(from currentColor l c h / 0.1);
  }
}

/* Icon button variant */
.media-minimal-skin .media-button--icon {
  display: grid;
  width: 2.375rem;
  aspect-ratio: 1;
  padding: 0;

  &:active {
    scale: 0.9;
  }

  & .media-icon__container {
    display: grid;
  }

  & .media-icon {
    grid-area: 1 / 1;
    transition-behavior: allow-discrete;
    transition-property: display, opacity;
    transition-duration: 150ms;
    transition-timing-function: ease-out;
    filter: drop-shadow(0 1px 0 var(--media-current-shadow-color));
  }
}

/* Seek button */
.media-minimal-skin .media-button--seek {
  & .media-icon__label {
    position: absolute;
    right: -1px;
    bottom: -3px;
    font-size: 10px; /* Hard coded due to size limitations. */
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.05em;
  }

  &:has(.media-icon--flipped) .media-icon__label {
    right: unset;
    left: -1px;
  }
}

/* Playback rate button */
.media-minimal-skin .media-button--playback-rate {
  padding: 0;
  font-variant-numeric: tabular-nums;

  &::after {
    width: 4ch;
    content: attr(data-rate) "\00D7";
  }

  &[data-inline-rate-label]::after {
    content: none;
  }
}

/* Settings button */
.media-minimal-skin .media-button--settings {
  display: none;

  & .media-icon--settings {
    transition: transform 150ms ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      transition-duration: 0ms;
    }
  }

  &[aria-expanded="true"] .media-icon--settings {
    transform: rotate(90deg);
  }
}

.media-minimal-skin .media-button-group:has([data-availability="available"]) .media-button--settings {
  display: grid;
}

/* Live button — wide pill button with a status dot (gray → red at the live
   edge) rendered via ::before, and "LIVE" text rendered as the button's own
   text content. */
.media-minimal-skin .media-button--live {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  width: auto;
  aspect-ratio: auto;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &::before {
    display: inline-block;
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    content: "";
    background-color: oklch(from currentColor l c h / 0.4);
    border-radius: 50%;
    transition: background-color 150ms ease-out;
  }

  &[data-live-edge]::before {
    background-color: oklch(0.65 0.22 27);
  }
}

/* ==========================================================================
   Button Groups
   ========================================================================== */

.media-minimal-skin .media-button-group {
  display: flex;
  gap: 0.075rem;
  align-items: center;

  @container media-root (width > 42rem) {
    gap: 0.125rem;
  }
}

/* ==========================================================================
   Badge
   ========================================================================== */

.media-minimal-skin .media-badge {
  padding: 0.25rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1;
  color: oklch(from currentColor l c h / 0.85);
  white-space: nowrap;
  background-color: oklch(from currentColor l c h / 0.1);
  border-radius: 0.25rem;
}

/* ==========================================================================
   Icons
   ========================================================================== */

.media-minimal-skin .media-icon__container {
  position: relative;
}

.media-minimal-skin .media-icon {
  flex-shrink: 0;
  width: var(--media-icon-size);
  height: var(--media-icon-size);
}
.media-minimal-skin .media-icon--flipped {
  scale: -1 1;
}

/* ==========================================================================
   Poster Image
   ========================================================================== */

.media-minimal-skin media-poster,
.media-minimal-skin > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transition: opacity 0.25s;
}
.media-minimal-skin media-poster:not([data-visible]),
.media-minimal-skin > img:not([data-visible]),
.media-minimal-skin > img[data-visible]:not([data-loaded]) {
  opacity: 0;
}
.media-minimal-skin media-poster ::slotted(img),
.media-minimal-skin media-poster img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: var(--media-object-fit, contain);
  object-position: var(--media-object-position, center);
  border-radius: var(--media-video-border-radius);
}
.media-minimal-skin > img {
  object-fit: var(--media-object-fit, contain);
  object-position: var(--media-object-position, center);
  border-radius: inherit;
}

/* Blurred placeholder: HTML path targets media-poster::before, React path targets the skin container::before */
.media-minimal-skin media-poster::before,
.media-minimal-skin::before {
  position: absolute;
  inset: 0;
  pointer-events: none;
  content: "";
  background-image: var(--media-poster-placeholder, none);
  background-repeat: no-repeat;
  background-position: var(--media-object-position, center);
  background-size: var(--media-object-fit, contain);
  filter: blur(var(--media-poster-placeholder-blur, 20px));
}

/* React path: hidden by default, transitions in while poster loads */
.media-minimal-skin::before {
  opacity: 0;
  transition: opacity 0.25s;
}
/* Show placeholder while the poster is visible but not yet loaded */
.media-minimal-skin:has(img[data-visible]:not([data-loaded]))::before {
  opacity: 1;
}

.media-minimal-skin:fullscreen media-poster ::slotted(img),
.media-minimal-skin:fullscreen media-poster img,
.media-minimal-skin:fullscreen > img {
  object-fit: contain;
}

/* ==========================================================================
   Media thumbnail
   ========================================================================== */

.media-minimal-skin .media-thumbnail {
  pointer-events: none;

  & .media-thumbnail__image-wrapper {
    position: relative;
    background-color: oklch(0 0 0 / 0.9);
    border-radius: 0.5rem;
  }
  & .media-thumbnail__image {
    display: block;
    border-radius: inherit;
  }

  & .media-thumbnail__time {
    display: block;
    margin-top: 0.5rem;
    text-align: center;
  }

  & .media-overlay {
    opacity: 1;
  }

  & .media-thumbnail__spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    opacity: 0;
    translate: -50% -50%;
  }

  & .media-thumbnail__image,
  & .media-thumbnail__spinner {
    transition: opacity 150ms ease-out;
  }

  &:not(:has(.media-thumbnail__image[data-loading])) {
    & .media-thumbnail__spinner {
      --media-spinner-animation: none;
    }
  }

  &:has(.media-thumbnail__image[data-loading]) {
    & .media-thumbnail__image {
      opacity: 0;
    }
    & .media-thumbnail__spinner {
      opacity: 1;
    }
  }
}

/* ==========================================================================
   Slider
   ========================================================================== */

.media-minimal-skin .media-slider {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  border-radius: calc(infinity * 1px);

  &[data-orientation="horizontal"] {
    width: 100%;
    min-width: 5rem;
    height: 2rem;
  }

  &[data-orientation="vertical"] {
    width: 2rem;
    height: 4.5rem;
  }
}

/* Track */
.media-minimal-skin .media-slider__track {
  position: relative;
  overflow: hidden;
  user-select: none;
  background-color: oklch(from currentColor l c h / 0.2);
  border-radius: inherit;
  isolation: isolate;

  &[data-orientation="horizontal"] {
    width: 100%;
    height: 0.1875rem;
  }

  &[data-orientation="vertical"] {
    width: 0.1875rem;
    height: 100%;
  }
}

/* Thumb */
.media-minimal-skin .media-slider__thumb {
  position: absolute;
  z-index: 10;
  width: 0.75rem;
  height: 0.75rem;
  user-select: none;
  outline: 2px solid transparent;
  outline-offset: -2px;
  background-color: currentColor;
  border-radius: calc(infinity * 1px);
  box-shadow:
    0 0 0 1px var(--media-current-shadow-color, oklch(0 0 0 / 0.15)),
    0 1px 3px 0 oklch(0 0 0 / 0.15),
    0 1px 2px -1px oklch(0 0 0 / 0.15);
  opacity: 0;
  transform-origin: center;
  scale: 0.7;
  translate: -50% -50%;
  transition-timing-function: ease-out;
  transition-duration: 150ms;
  transition-property: opacity, scale, outline-offset;

  &[data-orientation="horizontal"] {
    top: 50%;
    left: var(--media-slider-fill);
  }

  &[data-orientation="vertical"] {
    top: calc(100% - var(--media-slider-fill));
    left: 50%;
  }

  &:focus-visible {
    outline-color: currentColor;
    outline-offset: 2px;
  }
}

.media-minimal-skin .media-slider:hover .media-slider__thumb,
.media-minimal-skin .media-slider:focus-within .media-slider__thumb,
.media-minimal-skin .media-slider__thumb--persistent {
  opacity: 1;
  scale: 1;
}

/* Preview */
.media-minimal-skin .media-slider__preview {
  & .media-slider__value,
  &::before {
    opacity: 0;
    scale: 0.5;
    transition-timing-function: ease-out;
    transition-duration: 200ms;
    transition-property: opacity, scale;
  }

  & .media-slider__value {
    position: absolute;
    bottom: 1.5rem;
    text-shadow: 0 1px 0 var(--media-current-shadow-color);
    filter: blur(8px);
    translate: -50% 0.5rem;
    transition-property: filter, opacity, scale, translate;
  }

  &::before {
    display: block;
    content: "";
    background-color: oklch(from currentColor l c h / 0.35);
  }

  &[data-pointing] .media-slider__value,
  &[data-pointing]:not([data-dragging])::before {
    opacity: 1;
    scale: 1;
  }
  &[data-pointing] .media-slider__value {
    filter: blur(0);
    translate: -50% 0;
  }

  &[data-orientation="horizontal"]::before {
    min-width: 1px;
    height: 1.25rem;
  }

  &[data-orientation="vertical"]::before {
    width: 1.25rem;
    min-height: 1px;
  }
}

/* Shared track fills */
.media-minimal-skin .media-slider__buffer,
.media-minimal-skin .media-slider__fill {
  position: absolute;
  pointer-events: none;
  border-radius: inherit;
}

.media-minimal-skin .media-slider__buffer[data-orientation="horizontal"],
.media-minimal-skin .media-slider__fill[data-orientation="horizontal"] {
  inset-block: 0;
  left: 0;
}

.media-minimal-skin .media-slider__buffer[data-orientation="vertical"],
.media-minimal-skin .media-slider__fill[data-orientation="vertical"] {
  inset-inline: 0;
  bottom: 0;
}

/* Buffer */
.media-minimal-skin .media-slider__buffer {
  background-color: oklch(from currentColor l c h / 0.2);
  transition-timing-function: ease-out;
  transition-duration: 0.25s;

  &[data-orientation="horizontal"] {
    width: var(--media-slider-buffer);
    transition-property: width;
  }

  &[data-orientation="vertical"] {
    height: var(--media-slider-buffer);
    transition-property: height;
  }
}

/* Fill */
.media-minimal-skin .media-slider__fill {
  background-color: currentColor;

  &[data-orientation="horizontal"] {
    width: var(--media-slider-fill);
  }

  &[data-orientation="vertical"] {
    height: var(--media-slider-fill);
  }
}

/* Dragging — thumb and fill follow the pointer position */
.media-minimal-skin .media-slider[data-dragging] .media-slider__thumb[data-orientation="horizontal"] {
  left: var(--media-slider-pointer);
}

.media-minimal-skin .media-slider[data-dragging] .media-slider__thumb[data-orientation="vertical"] {
  top: calc(100% - var(--media-slider-pointer));
}

.media-minimal-skin .media-slider[data-dragging] .media-slider__fill[data-orientation="horizontal"] {
  width: var(--media-slider-pointer);
}

.media-minimal-skin .media-slider[data-dragging] .media-slider__fill[data-orientation="vertical"] {
  height: var(--media-slider-pointer);
}

/* ==========================================================================
   Popups & Animations
   ========================================================================== */

.media-minimal-skin {
  --media-popup-transition:
    opacity var(--media-popup-transition-timing-function) var(--media-popup-transition-duration),
    filter var(--media-popup-transition-timing-function) var(--media-popup-transition-duration),
    transform var(--media-popup-transition-timing-function) var(--media-popup-transition-duration),
    scale var(--media-popup-transition-timing-function) var(--media-popup-transition-duration);
}

.media-minimal-skin .media-popover,
.media-minimal-skin .media-tooltip {
  --popup-translate-distance: 0.5rem;
  margin: 0;
  overflow: visible;
  color: inherit;
  border: 0;
  transition: var(--media-popup-transition);

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    filter: blur(4px);
    /* We have to use transform here for translate as the translate property is used for positioning by core. */
    transform: translate(var(--popup-translate-x-distance, 0), var(--popup-translate-y-distance, 0));
    scale: 0.95;
  }

  &[data-ending-style] {
    transform: none;
    /* Speed up the exit transition. */
    transition-duration: max(0ms, calc(var(--media-popup-transition-duration) - 50ms));
  }

  &[data-side="top"] {
    --popup-translate-y-distance: var(--popup-translate-distance);
    transform-origin: bottom;
  }
  &[data-side="bottom"] {
    --popup-translate-y-distance: calc(var(--popup-translate-distance) * -1);
    transform-origin: top;
  }
  &[data-side="left"] {
    --popup-translate-x-distance: var(--popup-translate-distance);
    transform-origin: right;
  }
  &[data-side="right"] {
    --popup-translate-x-distance: calc(var(--popup-translate-distance) * -1);
    transform-origin: left;
  }

  /* Safe area between trigger and popup */
  &::before {
    position: absolute;
    pointer-events: inherit;
    content: "";
  }

  &[data-side="top"]::before,
  &[data-side="bottom"]::before {
    inset-inline: 0;
    width: 100%;
  }
  &[data-side="top"]::before {
    top: 100%;
  }
  &[data-side="bottom"]::before {
    bottom: 100%;
  }

  &[data-side="left"]::before,
  &[data-side="right"]::before {
    inset-block: 0;
    height: 100%;
  }
  &[data-side="left"]::before {
    left: 100%;
  }
  &[data-side="right"]::before {
    right: 100%;
  }
}

.media-minimal-skin .media-popover {
  &[data-side="top"]::before,
  &[data-side="bottom"]::before {
    height: var(--media-popover-side-offset);
  }
  &[data-side="left"]::before,
  &[data-side="right"]::before {
    width: var(--media-popover-side-offset);
  }
}

.media-minimal-skin .media-tooltip {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem; /* 12px at 100% font size */
  color: var(--media-tooltip-text-color);
  white-space: nowrap;
  /* Tooltips are labels, not hoverable surfaces. Safari can place the
     edge-constrained Play tooltip partly over its trigger; allowing that
     popup to win hit testing causes a rapid trigger leave/re-enter loop and
     flashes the text-selection cursor over the button. */
  pointer-events: none;
  user-select: none;
  background-color: var(--media-tooltip-background-color);
  border-radius: 0.5rem;
  box-shadow:
    0 0 0 1px var(--media-tooltip-border-color),
    0 4px 6px -1px oklch(0 0 0 / 0.2),
    0 2px 4px -2px oklch(0 0 0 / 0.2);
  backdrop-filter: var(--media-tooltip-backdrop-filter);

  /* `display: flex` must not apply while closed — it overrides UA `[popover]` hiding. */
  &[data-open] {
    display: flex;
    column-gap: 0.25rem;
    align-items: center;
  }

  &[data-side="top"]::before,
  &[data-side="bottom"]::before {
    height: var(--media-tooltip-side-offset);
  }
  &[data-side="left"]::before,
  &[data-side="right"]::before {
    width: var(--media-tooltip-side-offset);
  }

  & .media-tooltip__kbd {
    min-width: 1.5em;
    padding: 0.1em;
    margin-right: -0.25rem;
    font-family: inherit;
    font-size: 90%;
    font-weight: 600;
    line-height: 1.25;
    text-align: center;
    background-color: oklch(from currentColor l c h / 0.15);
    border-radius: 0.25rem;
  }
}

.media-minimal-skin .media-popover--volume:has(media-volume-slider[data-availability="unsupported"]) {
  display: none;
}

/* ==========================================================================
   Menus
   Note: Menus use `.media-popover` styles for positioning and transitions.
   ========================================================================== */

.media-minimal-skin .media-menu {
  --menu-transition-duration: 250ms;
  --menu-max-height: 14rem;
  --menu-padding: 0.5rem;
  --menu-border-radius: 1rem;
  --menu-item-border-radius: calc(var(--menu-border-radius) - var(--menu-padding));
  box-sizing: border-box;
  max-width: var(--media-popover-available-width, none);
  max-height: min(var(--media-popover-available-height, var(--menu-max-height)), var(--menu-max-height));
  padding: var(--menu-padding);
  overflow: auto;
  overscroll-behavior: none;
  background-color: var(--media-popover-background-color);
  border-radius: var(--menu-border-radius);
  box-shadow:
    0 0 0 1px var(--media-popover-border-color),
    0 4px 6px -1px oklch(0 0 0 / 0.1),
    0 2px 4px -2px oklch(0 0 0 / 0.1);
  backdrop-filter: var(--media-popover-backdrop-filter);

  @media (prefers-reduced-motion: reduce) {
    --menu-transition-duration: 0ms;
  }

  & .media-menu__panel {
    position: absolute;
    inset: 0;
    padding: var(--menu-padding);
    overflow: auto;
    overscroll-behavior: none;
    outline: none;
    translate: 0 0;
    transition-timing-function: ease-in-out;
    transition-duration: var(--menu-transition-duration);
    transition-property: translate, filter;
    will-change: translate, filter;

    &[data-starting-style],
    &[data-ending-style] {
      overflow: hidden;
    }

    /* Root settings view — slides out when a submenu is active */
    &[data-menu-root-view][data-menu-view-state="inactive"] {
      filter: blur(8px);
      translate: -100% 0;
    }
  }

  /* While the menu height animates between views, a panel momentarily
     overflows and a classic (space-taking) scrollbar flashes in, shoving
     the content sideways. Hide menu scrollbars entirely — the lists are
     short and stay wheel/touch scrollable. */
  &,
  & .media-menu__panel {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  & .media-menu__panel {
    /* Submenu panels — slide in/out alongside the root view */
    &[data-submenu] {
      z-index: 10;

      &:not([data-open], [data-ending-style]) {
        translate: -100% 0;
        transition-property: none;
      }

      &[data-starting-style],
      &[data-ending-style] {
        pointer-events: none;
        filter: blur(8px);
      }

      &[data-starting-style][data-direction="forward"],
      &[data-ending-style][data-direction="back"] {
        translate: 100% 0;
      }

      &[data-ending-style][data-direction="forward"],
      &[data-starting-style][data-direction="back"] {
        translate: -100% 0;
      }
    }
  }

  & .media-menu__separator {
    margin: 0.25rem 0;
    border-bottom: 1px solid oklch(1 0 0 / 0.1);
  }

  /* Stock skin used an anchor-positioned sliding highlight here; rows use
     a plain per-row hover wash instead. */
  & .media-menu__group {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  & .media-menu__item,
  & .media-menu__back {
    display: flex;
    gap: 0.375rem;
    align-items: center;
    padding: 0.375rem 0.5rem;
    text-align: left;
    /* Menu.Back renders a native <button>; strip the UA chrome (light
       ButtonFace fill, ButtonText color, border) that rows, as <div>s,
       never had. */
    appearance: none;
    background: none;
    border: none;
    color: inherit;
    white-space: nowrap;
    text-shadow: 0 1px 0 var(--media-current-shadow-color);
    cursor: pointer;
    user-select: none;
    outline: 2px solid transparent;
    outline-offset: -2px;
    border-radius: var(--menu-item-border-radius);
    transition: background-color, color;
    transition-timing-function: ease-out;
    transition-duration: 100ms;

    &:focus-visible {
      outline-color: currentColor;
      outline-offset: 2px;
    }

    &:hover,
    &[data-highlighted] {
      background-color: oklch(from currentColor l c h / 0.1);
    }

    & .media-icon {
      flex-shrink: 0;
      color: oklch(from currentColor l c h / 0.5);
      filter: drop-shadow(0 1px 0 var(--media-current-shadow-color));
    }
  }

  & .media-menu__indicator {
    flex-shrink: 0;
    margin-right: -0.25rem;
    margin-left: auto;
    opacity: 0;
  }

  /* Submenu rows share a real icon column. The fixed slot—not the individual
     glyph's intrinsic width—owns label alignment. */
  & .media-menu__item--submenu {
    display: grid;
    grid-template-columns: 1.5rem minmax(0, 1fr) auto;
    column-gap: 0.5rem;
  }

  & .media-menu__leading-icon {
    display: grid;
    width: 1.5rem;
    height: 1.5rem;
    place-items: center;

    & .media-icon {
      width: 1.125rem;
      height: 1.125rem;
    }
  }

  & .media-menu__track-label {
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
  }

  & .media-icon--audio-track {
    width: 1.125rem;
    height: 1.125rem;
  }

  & .media-menu__item {
    justify-content: space-between;
    font-variant-numeric: tabular-nums;
    color: inherit;

    &[aria-disabled="true"] {
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.5;
    }

    &[aria-checked="true"] .media-menu__indicator {
      opacity: 1;
    }

    &[data-availability="unavailable"],
    &[data-availability="unsupported"] {
      display: none;
    }
  }

  & .media-menu__tier {
    padding-top: 1px;
    padding-left: 0.125rem;
    font-size: 0.5625rem;
    font-weight: 600;
    line-height: 1;
    color: oklch(from currentColor l c h / 0.7);
  }

  & .media-menu__back {
    width: 100%;
    margin-bottom: 0.125rem;
  }

  & .media-menu__hint {
    display: inline-flex;
    gap: 0.25rem;
    align-items: center;
    min-width: 0;
    padding-left: 0.5rem;
    margin-left: auto;
    font-size: 0.75rem;
    color: oklch(from currentColor l c h / 0.65);
  }

  & .media-menu__hint-label {
    max-width: 6rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .media-menu__chevron {
    width: 0.875rem;
    height: 0.875rem;
  }

  /* Settings menu */
  &.media-menu--settings {
    width: var(--media-menu-width);
    min-width: 12rem;
    height: var(--media-menu-height);
    overflow: hidden;
    /* Add height and width transitions. */
    transition:
      var(--media-popup-transition),
      height var(--media-popup-transition-timing-function) var(--menu-transition-duration),
      width var(--media-popup-transition-timing-function) var(--menu-transition-duration);

    /* Don't transition width and height on open/close. */
    &[data-starting-style],
    &[data-ending-style] {
      transition: var(--media-popup-transition);
    }
  }
}

/* ==========================================================================
   Native Caption Track
   ========================================================================== */

.media-minimal-skin {
  --media-caption-track-duration: var(--media-controls-transition-duration);
  --media-caption-track-delay: 25ms;
  --media-caption-track-y: -0.5rem;

  &:has(.media-controls[data-visible]) {
    --media-caption-track-y: -5rem;
  }

  @container media-root (width > 42rem) {
    &:has(.media-controls[data-visible]) > * {
      --media-caption-track-y: -3rem;
    }
  }
}

.media-minimal-skin video::-webkit-media-text-track-container {
  z-index: 1;
  font-family: inherit;
  scale: 0.98;
  translate: 0 var(--media-caption-track-y);
  transition: translate var(--media-caption-track-duration) ease-out;
  transition-delay: var(--media-caption-track-delay);
}

/* ==========================================================================
   Input Feedback
   ========================================================================== */

.media-minimal-skin .media-input-feedback {
  position: absolute;
  inset-inline: 0;
  top: 0;
  bottom: 3.5rem; /* Shift up a little in smaller containers */
  /* Above the PiP cover (z 5) so state toasts stay readable over it. */
  z-index: 12;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  justify-items: center;
  overflow: hidden;
  color: var(--media-color-primary, oklch(1 0 0));
  pointer-events: none;
  border-radius: inherit;

  @container media-root (width > 24rem) {
    bottom: 0;
  }
}

/* --- Feedback islands ------------------------------------------------------- */

.media-minimal-skin .media-input-feedback-island {
  position: absolute;
  inset-inline: 0;
  top: 0;
  display: flex;
  justify-content: center;
  padding-top: 0.75rem;
  padding-bottom: 8rem;
  color: inherit;
  text-shadow: 0 1px 0 var(--media-current-shadow-color);
  pointer-events: none;
  transform-origin: top center;
  transition-timing-function: ease-out;
  transition-duration: 100ms;

  /* App pill language — the same dark glass + cool hairline as the
     audio-feed and route chips, replacing the default full-width smear. */
  .media-input-feedback-island__content {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.25rem;
    background: rgba(20, 22, 31, 0.6);
    border: 1px solid rgba(167, 178, 216, 0.24);
    border-radius: calc(infinity * 1px);
    -webkit-backdrop-filter: blur(16px) saturate(140%);
    backdrop-filter: blur(16px) saturate(140%);
  }

  .media-icon {
    display: none;
    flex-shrink: 0;
    filter: drop-shadow(0 1px 0 var(--media-current-shadow-color));
  }

  .media-input-feedback-island__value {
    margin-left: auto;
  }

  @media (pointer: fine) {
    transition-property: translate, filter, opacity;
    will-change: translate, filter, opacity;
  }

  @media (pointer: coarse) {
    transition-property: translate, opacity;
    will-change: translate, opacity;
  }

  @media (pointer: fine) and (prefers-reduced-motion: no-preference) {
    transition-property: translate, filter, opacity;
  }

  @media (prefers-reduced-transparency: reduce) or (prefers-contrast: more) {
    .media-input-feedback-island__content {
      background: var(--media-controls-background-color);
      border-radius: 0.5rem;
    }
  }

  /* Default hidden state */
  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transition-timing-function: ease-in;
    transition-duration: 400ms;

    @media (pointer: fine) and (prefers-reduced-motion: no-preference) {
      filter: blur(8px);
    }

    @media (prefers-reduced-motion: no-preference) {
      &[data-ending-style] {
        translate: 0 -100%;
      }
    }
  }
}

.media-minimal-skin .media-input-feedback-island--volume {
  .media-input-feedback-island__content {
    width: min(80%, 14rem);
  }

  .media-input-feedback-island__progress {
    --media-progress-fill: var(--media-volume-fill);
    width: 100%;
    height: 0.1875rem;
    background-image: linear-gradient(
      to right,
      currentColor 0%,
      currentColor var(--media-progress-fill),
      oklch(from currentColor l c h / 0.2) var(--media-progress-fill),
      oklch(from currentColor l c h / 0.2) 100%
    );
    border-radius: calc(Infinity * 1px);
    box-shadow: 0 1px 0 var(--media-current-shadow-color-subtle);
  }

  &[data-level="high"] .media-icon--volume-high,
  &[data-level="low"] .media-icon--volume-low,
  &[data-level="off"] .media-icon--volume-off {
    display: block;
  }

  @media (prefers-reduced-motion: no-preference) {
    &[data-min] .media-input-feedback-island__content,
    &[data-max] .media-input-feedback-island__content {
      animation: media-shake 300ms ease-in-out;
    }
  }
}

.media-minimal-skin .media-input-feedback-island--status {
  &[data-status="captions-on"] .media-icon--captions-on,
  &[data-status="captions-off"] .media-icon--captions-off,
  &[data-status="fullscreen"] .media-icon--fullscreen-enter,
  &[data-status="exit-fullscreen"] .media-icon--fullscreen-exit,
  &[data-status="pip"] .media-icon--pip-enter,
  &[data-status="exit-pip"] .media-icon--pip-exit {
    display: block;
  }
}

/* --- Bubble ---------------------------------------------------------------- */

.media-minimal-skin .media-input-feedback-bubble {
  display: grid;
  grid-row: 1;
  grid-column: 2;
  place-content: center;
  padding: 1rem;
  text-align: center;

  /* Central bubble (play, pause) */
  &:not([data-direction]) {
    transition-timing-function: ease-out;
    transition-duration: 200ms;
    transition-property: opacity, scale;

    &[data-starting-style],
    &[data-ending-style] {
      opacity: 0;
      scale: 0.85;
    }

    &[data-ending-style] {
      transition-timing-function: ease-in;
      transition-duration: 100ms;
    }

    @media (prefers-reduced-motion: reduce) {
      transition-duration: 50ms;
      transition-property: opacity;
    }
  }

  /* Directional bubbles (seek) */
  &[data-direction] {
    gap: 0.25rem;

    @container media-root (width > 24rem) {
      padding: 1.5rem;
    }
  }

  &[data-direction="backward"] {
    grid-column: 1;
    justify-self: left;
  }

  &[data-direction="forward"] {
    grid-column: 3;
    justify-self: right;
  }

  /* Icons */
  .media-icon {
    display: none;
    width: calc(var(--media-icon-size) * 2);
    height: calc(var(--media-icon-size) * 2);
  }

  .media-icon--seek {
    width: calc(var(--media-icon-size) * 1.5);
    height: calc(var(--media-icon-size) * 1.5);
  }

  &[data-direction] .media-icon--seek,
  &[data-status="pause"] .media-icon--pause,
  &[data-status="play"] .media-icon--play {
    display: block;
  }

  &[data-direction="backward"] .media-icon--seek {
    scale: -1 1;
  }

  @media (prefers-reduced-motion: no-preference) {
    .media-icon--seek {
      transition-timing-function: ease-in-out;
      transition-duration: 200ms;
      transition-property: translate, opacity;
    }

    &[data-starting-style] .media-icon--seek,
    &[data-ending-style] .media-icon--seek {
      opacity: 0;
    }

    &[data-direction="forward"][data-starting-style] .media-icon--seek {
      translate: -60% 0;
    }
    &[data-direction="backward"][data-starting-style] .media-icon--seek {
      translate: 60% 0;
    }
  }
}

/* ==========================================================================
   Icon State Visibility for Video Skins

   Data-attribute-driven visibility rules for multi-state icon buttons.
   Uses :is() with both element selectors (for HTML custom element wrappers)
   and class selectors (for React rendered SVG elements).
   ========================================================================== */

/* --- All icons hidden by default --- */

.media-button--play .media-icon--restart,
.media-button--play .media-icon--play,
.media-button--play .media-icon--pause,
.media-button--mute .media-icon--volume-off,
.media-button--mute .media-icon--volume-low,
.media-button--mute .media-icon--volume-high,
.media-button--fullscreen .media-icon--fullscreen-enter,
.media-button--fullscreen .media-icon--fullscreen-exit,
.media-button--pip .media-icon--pip-enter,
.media-button--pip .media-icon--pip-exit,
.media-button--airplay .media-icon--airplay-enter,
.media-button--airplay .media-icon--airplay-exit,
.media-button--captions .media-icon--captions-off,
.media-button--captions .media-icon--captions-on {
  display: none;
  opacity: 0;
}

/* --- Active icon per state --- */

/* Play: ended → restart */
.media-button--play[data-ended] .media-icon--restart,
/* Play: paused or not yet started (not ended) → play */
.media-button--play:not([data-ended])[data-paused] .media-icon--play,
.media-button--play:not([data-ended]):not([data-started]) .media-icon--play,
/* Play: started and not paused/ended → pause */
.media-button--play[data-started]:not([data-paused]):not([data-ended]) .media-icon--pause,
/* Mute: muted → volume off */
.media-button--mute[data-muted] .media-icon--volume-off,
/* Mute: volume low (not muted) → volume low */
.media-button--mute:not([data-muted])[data-volume-level="low"] .media-icon--volume-low,
/* Mute: volume high (not muted, not low) → volume high */
.media-button--mute:not([data-muted]):not([data-volume-level="low"]) .media-icon--volume-high,
/* Fullscreen: not fullscreen → enter */
.media-button--fullscreen:not([data-fullscreen]) .media-icon--fullscreen-enter,
/* Fullscreen: fullscreen → exit */
.media-button--fullscreen[data-fullscreen] .media-icon--fullscreen-exit,
/* Picture-in-Picture: not active → enter */
.media-button--pip:not([data-pip]) .media-icon--pip-enter,
/* Picture-in-Picture: active → exit */
.media-button--pip[data-pip] .media-icon--pip-exit,
/* AirPlay: not connected → enter */
.media-button--airplay:not([data-airplay-state="connected"]) .media-icon--airplay-enter,
/* AirPlay: connected → exit */
.media-button--airplay[data-airplay-state="connected"] .media-icon--airplay-exit,
/* Captions: not active → captions off */
.media-button--captions:not([data-active]) .media-icon--captions-off,
/* Captions: active → captions on */
.media-button--captions[data-active] .media-icon--captions-on {
  display: block;
  opacity: 1;
}

/* While PiP is active the in-page surface shows Chrome's unstylable grey
   placeholder — cover it with the app's own quiet state: black canvas, one
   overline whisper. Sits above the video (and the poster ::before) but
   below the controls (z 10) and feedback toasts (z 12). */
.media-minimal-skin:has(.media-button--pip[data-pip])::after {
  content: "Playing in picture-in-picture";
  position: absolute;
  inset: 0;
  z-index: 5;
  display: grid;
  place-content: center;
  pointer-events: none;
  background: oklch(0 0 0);
  border-radius: inherit;
  color: rgb(156, 162, 188);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

/* --- Pause keyframe animations on inactive icons --- */

/* The airplay-exit SVG defines its keyframes against CSS variables (mirroring
   the spinner pattern). When the AirPlay session isn't active the SVG is
   still in the DOM — just `display: none` — so its animations would keep
   running. Set the variables to `none` to short-circuit the keyframes. */
.media-button--airplay:not([data-airplay-state="connected"]) {
  --media-icon--airplay__fill-animation: none;
  --media-icon--airplay__triangle-animation: none;
}

/* -------------------------------------------------------------------------- */
/* Global @keyframes for all video skins (CSS & Tailwind)                     */
/* -------------------------------------------------------------------------- */

@keyframes media-shake {
  0%,
  100% {
    translate: 0 0;
  }
  20% {
    translate: -6px 0;
  }
  40% {
    translate: 4px 0;
  }
  60% {
    translate: -2px 0;
  }
  80% {
    translate: 1px 0;
  }
}

/* -------------------------------------------------------------------------- */
/* Global @properties for all video skins (CSS & Tailwind)                    */
/* -------------------------------------------------------------------------- */

@property --media-progress-fill {
  syntax: "<percentage>";
  inherits: true;
  initial-value: 0%;
}


/* ==========================================================================
   Root
   ========================================================================== */

.media-minimal-skin--video {
  --media-border-color: oklch(0 0 0 / 0.15);
  --media-video-border-radius: var(--media-border-radius, 0);
  --media-controls-background-color: transparent;
  --media-controls-transition-duration: 100ms;
  --media-controls-transition-timing-function: ease-out;
  --media-error-dialog-transition-duration: 150ms;
  --media-error-dialog-transition-delay: 100ms;
  --media-error-dialog-transition-timing-function: ease-out;
  --media-popup-transition-duration: 100ms;
  --media-popup-transition-timing-function: ease-out;
  --media-tooltip-background-color: oklch(0 0 0 / 0.5);
  --media-tooltip-border-color: oklch(1 0 0 / 0.1);
  --media-tooltip-backdrop-filter: blur(16px) saturate(1.5);
  --media-tooltip-text-color: currentColor;
  --media-tooltip-side-offset: 0.5rem;
  --media-tooltip-boundary-offset: 0.5rem;
  --media-popover-background-color: var(--media-tooltip-background-color);
  --media-popover-border-color: var(--media-tooltip-border-color);
  --media-popover-backdrop-filter: var(--media-tooltip-backdrop-filter);
  --media-popover-side-offset: 1.5rem;
  --media-popover-boundary-offset: var(--media-tooltip-boundary-offset);
  overflow: clip;
  background: oklch(0 0 0);

  @media (prefers-reduced-motion: reduce) {
    --media-error-dialog-transition-duration: 50ms;
    --media-error-dialog-transition-delay: 0ms;
    --media-popup-transition-duration: 0ms;
  }

  @media (prefers-color-scheme: dark) {
    --media-border-color: oklch(1 0 0 / 0.15);
  }

  @media (prefers-reduced-transparency: reduce) or (prefers-contrast: more) {
    --media-controls-background-color: oklch(0 0 0);
    --media-tooltip-background-color: oklch(0 0 0);
  }

  @container media-root (width > 42rem) {
    & > * {
      --media-popover-side-offset: 0.5rem;
    }
  }

  &:has(.media-controls:not([data-visible])) {
    /* Slight delay to hide controls on non-touch devices after interaction */
    @media (pointer: fine) {
      --media-controls-transition-duration: 300ms;
    }
    @media (pointer: coarse) {
      --media-controls-transition-duration: 150ms;
    }
    @media (prefers-reduced-motion: reduce) {
      --media-controls-transition-duration: 50ms;
    }
  }


  /* Fullscreen */
  &:fullscreen {
    --media-border-radius: 0;
  }
}

/* ==========================================================================
   Error Dialog
   ========================================================================== */

.media-minimal-skin--video .media-error {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  outline: none;
}

.media-minimal-skin--video .media-error__dialog {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 16rem;
  padding: 1rem;
  color: oklch(1 0 0);
  text-shadow: 0 1px 0 oklch(0 0 0 / 0.5);
  pointer-events: auto;
  transition-delay: var(--media-error-dialog-transition-delay);
  transition-timing-function: var(--media-error-dialog-transition-timing-function);
  transition-duration: var(--media-error-dialog-transition-duration);
  transition-property: opacity, scale;
}

.media-minimal-skin--video .media-error[data-starting-style] .media-error__dialog,
.media-minimal-skin--video .media-error[data-ending-style] .media-error__dialog {
  opacity: 0;
  scale: 0.5;
}
.media-minimal-skin--video .media-error[data-ending-style] .media-error__dialog {
  transition-delay: 0ms;
}

.media-minimal-skin--video .media-error__content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.375rem 0;
}

.media-minimal-skin--video .media-error__title {
  font-size: 1.125rem;
}

.media-minimal-skin--video .media-error[data-open] ~ .media-controls {
  display: none;
}

/* ==========================================================================
  Controls (hide/show behavior)
  ========================================================================== */

.media-minimal-skin--video .media-controls {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  /* The base .media-controls rule centers items; with a column layout the
     cross axis is horizontal, so re-stretch the rows to full width. */
  align-items: stretch;
  /* Give the button row a little breathing room above the rounded player edge. */
  gap: 0.375rem;
  padding: 0.5rem 0.75rem 0.75rem;
  color: oklch(1 0 0);
  /* Bottom scrim instead of a floating pill — consistent regardless of the
     OS reduce-transparency setting. */
  background: linear-gradient(to top, oklch(0 0 0 / 0.72), oklch(0 0 0 / 0.3) 55%, transparent);
  border-radius: 0;
  transition-timing-function: var(--media-controls-transition-timing-function);
  transition-duration: var(--media-controls-transition-duration);

  @media (pointer: fine) {
    transition-property: translate, filter, opacity;
    will-change: translate, filter, opacity;
  }

  @media (pointer: coarse) {
    transition-property: translate, opacity;
    will-change: translate, opacity;
  }

  &:not([data-visible]) {
    pointer-events: none;
    opacity: 0;

    @media (pointer: fine) and (prefers-reduced-motion: no-preference) {
      filter: blur(8px);
    }

    @media (prefers-reduced-motion: no-preference) {
      translate: 0 100%;
    }
  }
}

.media-minimal-skin--video .media-controls__row {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.media-minimal-skin--video .media-controls__row .media-button-group--end {
  margin-left: auto;
}

/* Audience/Clean and Cast live above the Video.js container. Crossing into
   either would otherwise trigger Video.js's mouseleave inactivity path and
   collapse the controls underneath the pointer. While an external control
   owns hover/focus, it participates in the same visible chrome state. */
.media-minimal-skin--video.media-minimal-skin--controls-pinned
  .media-controls:not([data-visible]) {
  pointer-events: auto;
  opacity: 1;
  filter: none;
  translate: 0;
}

.media-minimal-skin--video.media-minimal-skin--controls-pinned
  .media-controls ~ .media-overlay {
  opacity: 1;
}

/* An autonomous queue promotion must inherit the outgoing hidden state even
   though the old media's `ended` event briefly marks Video.js controls as
   visible. Pointer/focus/keyboard intent clears this class immediately. */
.media-minimal-skin--video.media-minimal-skin--controls-suppressed:not(
    .media-minimal-skin--controls-pinned
  )
  .media-controls {
  pointer-events: none;
  opacity: 0;
  filter: none;
  translate: 0 100%;
}

.media-minimal-skin--video.media-minimal-skin--controls-suppressed:not(
    .media-minimal-skin--controls-pinned
  )
  .media-controls ~ .media-overlay {
  opacity: 0;
}

/* Transport grammar: play anchors, the dance pair hugs it, the ±10s pair
   sits outermost — a breath between the pairs keeps them reading as groups. */
.media-minimal-skin--video .media-controls__row .media-button--seek-back {
  margin-left: 0.375rem;
}

/* The speed control is its own label — the trigger reads the current rate. */
.media-minimal-skin--video .media-button--speed {
  width: 3rem;
  min-width: 3rem;
  height: 2.375rem;
  aspect-ratio: auto;
  padding-inline: 0.375rem;
}

.media-minimal-skin--video .media-speed-value {
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  line-height: 1;
}

/* Hide cursor when controls are hidden */
.media-minimal-skin--video:has(.media-controls:not([data-visible])) {
  cursor: none;
}

/* ==========================================================================
   Sliders
   ========================================================================== */

.media-minimal-skin--video .media-slider__track {
  box-shadow: 0 0 0 1px oklch(0 0 0 / 0.05);
}

/* Universal signature at every size: [current] ——— rail ——— [duration].
   Current stays bright, duration soft — same hierarchy as the app chrome. */
.media-minimal-skin--video .media-timeline-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
}

.media-minimal-skin--video .media-timeline-row__time {
  display: block;
  flex: 0 0 auto;
  min-width: 3ch;
  font-size: 0.785rem;
  font-weight: 400;
  line-height: 1;
  color: oklch(1 0 0 / 0.92);
  font-variant-numeric: tabular-nums;
}

.media-minimal-skin--video .media-timeline-row__time--duration {
  text-align: right;
  color: oklch(1 0 0 / 0.64);
}

.media-minimal-skin--video .media-timeline-row > .media-slider--time {
  flex: 1 1 auto;
  width: auto;
  min-width: 5rem;
  height: 2.5rem;
  min-height: 2.5rem;
  /* Keep the rail centered in a generous pointer target, then pull the
     transport row upward so the control still reads as one compact cluster. */
  margin-block: -0.875rem -1rem;
}

/* Brand gradient scrub fill — identical ramp to the native chrome. */
.media-minimal-skin--video .media-slider--time .media-slider__fill {
  background: linear-gradient(to right, #79D2F4, #A99BF8, #F2A0CC);
}

/* Phone-width player: a deliberately balanced remote, not a desktop toolbar
   squeezed into a card. Audio/configuration live left, transport remains
   mathematically centered, and viewing modes live right. AirPlay stays out of
   the compact bar (the app's cast surface and platform route UI own it), while
   double-tap gestures own ±10 second seeking. */
@container media-root (width <= 28.125rem) {
  .media-minimal-skin--video .media-controls {
    gap: 0;
  }

  .media-minimal-skin--video .media-timeline-row {
    flex: 0 0 1.5rem;
    gap: 0.5rem;
    min-height: 1.5rem;
    margin-bottom: -0.25rem;
  }

  .media-minimal-skin--video .media-timeline-row > .media-slider--time {
    height: 1.5rem;
    min-height: 1.5rem;
    margin: 0;
  }

  .media-minimal-skin--video .media-button--seek-back,
  .media-minimal-skin--video .media-button--seek-forward,
  .media-minimal-skin--video .media-controls__row .media-button--airplay {
    display: none;
  }

  .media-minimal-skin--video .media-controls__row {
    position: relative;
  }

  /* True-centered transport trio, native-style: the group spans the row and
     centers; only the buttons themselves take pointer events. */
  .media-minimal-skin--video .media-controls__row
    .media-button-group:not(.media-button-group--end) {
    position: absolute;
    inset-inline: 0;
    justify-content: center;
    pointer-events: none;
  }

  .media-minimal-skin--video .media-controls__row
    .media-button-group:not(.media-button-group--end) > * {
    pointer-events: auto;
  }

  .media-minimal-skin--video .media-button--previous {
    order: -1;
  }

  /* The environment group becomes two symmetric corner pairs:
     [volume settings]                [PiP fullscreen].
     Equal visual weight on both sides protects the centered transport trio. */
  .media-minimal-skin--video .media-controls__row .media-button-group--end {
    width: 100%;
    margin-left: 0;
  }

  .media-minimal-skin--video .media-controls__row .media-button--pip {
    margin-left: auto;
  }
}

/* A 320px phone leaves a ~272px player after page gutters. Tighten only the
   icon circles—not the hierarchy—so adjacent hit targets never overlap. */
@container media-root (width <= 20rem) {
  .media-minimal-skin--video .media-controls__row .media-button--icon {
    width: 2.125rem;
  }

  .media-minimal-skin--video .media-controls__row .media-button-group {
    gap: 0;
  }

  .media-minimal-skin--video .media-controls__row .media-button--speed {
    width: 3rem;
    min-width: 3rem;
    height: 2.125rem;
  }
}

/* ==========================================================================
   Popups & Animations
   ========================================================================== */

/* The settings menu opens directly above the transport row. On wide players
   the shared popover offset collapses to 0.5rem (see the width > 42rem rule),
   which lets the panel clip the scrubber sitting just above the buttons. */
.media-minimal-skin--video .media-menu--settings {
  --media-popover-side-offset: 1.25rem;
}

.media-minimal-skin--video .media-popover--volume {
  /* Keep the vertical slider visually tethered to its speaker button. */
  --media-popover-side-offset: 0.6rem;
  padding-block: 0.75rem;
  background: transparent;
  border-radius: 0.75rem;

  @media (prefers-reduced-transparency: reduce) or (prefers-contrast: more) {
    background: var(--media-controls-background-color);
  }

}

/* ==========================================================================
   Slider preview
   ========================================================================== */

.media-minimal-skin--video .media-slider__thumbnail {
  --media-slider-thumbnail-max-width: 11rem;
  --media-slider-thumbnail-max-height: 8rem;
  --media-slider-thumbnail-padding: -0.5rem;
  /**
    Inset is the difference between the container width and the slider (100%) width.
    We only add to the end as we render the time there.
  */
  --media-slider-thumbnail-inset: calc(100cqi - 100%);

  position: absolute;
  bottom: calc(50% + 0.5rem);
  left: clamp(
    calc(var(--media-slider-thumbnail-max-width) / 2 + var(--media-slider-thumbnail-padding)),
    var(--media-slider-pointer),
    calc(
      100% -
      var(--media-slider-thumbnail-max-width) /
      2 -
      var(--media-slider-thumbnail-padding) +
      var(--media-slider-thumbnail-inset)
    )
  );
  opacity: 0;
  filter: blur(8px);
  transform-origin: bottom;
  scale: 0.8;
  translate: -50%;
  transition-timing-function: ease-out;
  transition-duration: 150ms;
  transition-property: scale, opacity, filter;

  @container media-root (width > 42rem) {
    left: var(--media-slider-pointer);
  }

  & .media-thumbnail__image-wrapper {
    position: relative;

    &::after {
      position: absolute;
      inset: 0;
      content: "";
      border-radius: inherit;
      box-shadow:
        0 0 0 1px oklch(0 0 0 / 0.05),
        0 1px 3px 0 oklch(0 0 0 / 0.2),
        0 1px 2px -1px oklch(0 0 0 / 0.2);
    }
  }

  & .media-thumbnail__image {
    max-width: var(--media-slider-thumbnail-max-width);
    max-height: var(--media-slider-thumbnail-max-height);
  }
}
.media-minimal-skin--video .media-slider[data-pointing] .media-slider__thumbnail:has([role="img"]:not([data-hidden])) {
  opacity: 1;
  filter: blur(0);
  scale: 1;
}
.media-minimal-skin--video
  .media-slider__thumbnail:has([role="img"]:not([data-hidden]))
  + .media-slider__preview
  .media-slider__value {
  display: none;
}
